
import { useState, useEffect } from 'react';
import { X, Settings, Loader2, Play, ArrowRight, GitBranch, Layers, Map, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { StateNodeData, AslStateType, AslState } from '@/types/workflow-graph';

interface NodeSettingsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    nodeData: StateNodeData | null;
    onSave?: (nodeId: string, updates: Partial<StateNodeData>) => void;
    readOnly?: boolean;
    /** Available resources for Task states */
    availableResources?: {
        agents: Array<{ name: string; fqcn: string; description?: string }>;
        tools: Array<{ name: string; fqcn: string; description?: string }>;
        models: Array<{ name: string; fqcn: string; description?: string }>;
        workflows: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    };
}

const stateTypeIcons: Record<AslStateType, typeof Play> = {
    Task: Play,
    Pass: ArrowRight,
    Choice: GitBranch,
    Wait: Clock,
    Parallel: Layers,
    Map: Map,
    Succeed: CheckCircle2,
    Fail: XCircle,
};

const stateTypeColors: Record<AslStateType, { bg: string; text: string; border: string }> = {
    Task: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
    Pass: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
    Choice: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
    Wait: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200' },
    Parallel: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
    Map: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
    Succeed: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
    Fail: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
};

export default function NodeSettingsDrawer({
    isOpen,
    onClose,
    nodeData,
    onSave,
    readOnly = true,
    availableResources,
}: NodeSettingsDrawerProps) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<{
        label: string;
        comment: string;
        resource: string;
        resultPath: string;
        isEnd: boolean;
        next: string;
        seconds: number | undefined;
        error: string;
        cause: string;
    }>({
        label: '',
        comment: '',
        resource: '',
        resultPath: '',
        isEnd: false,
        next: '',
        seconds: undefined,
        error: '',
        cause: '',
    });

    // Reset form when nodeData changes
    useEffect(() => {
        if (isOpen && nodeData) {
            setFormData({
                label: nodeData.label || '',
                comment: nodeData.state.Comment || '',
                resource: nodeData.state.Resource || '',
                resultPath: nodeData.state.ResultPath || '',
                isEnd: nodeData.state.End || false,
                next: nodeData.state.Next || '',
                seconds: nodeData.state.Seconds,
                error: nodeData.state.Error || '',
                cause: nodeData.state.Cause || '',
            });
        }
    }, [isOpen, nodeData]);

    const handleSave = () => {
        if (!nodeData || readOnly) return;

        setLoading(true);

        const updatedState: Partial<AslState> = {
            ...nodeData.state,
            Comment: formData.comment || undefined,
        };

        // Type-specific updates
        if (nodeData.stateType === 'Task') {
            updatedState.Resource = formData.resource || undefined;
            updatedState.ResultPath = formData.resultPath || undefined;
        }

        if (nodeData.stateType === 'Wait') {
            updatedState.Seconds = formData.seconds;
        }

        if (nodeData.stateType === 'Fail') {
            updatedState.Error = formData.error || undefined;
            updatedState.Cause = formData.cause || undefined;
        }

        // Handle End/Next
        if (formData.isEnd) {
            updatedState.End = true;
            delete updatedState.Next;
        } else {
            delete updatedState.End;
            updatedState.Next = formData.next || undefined;
        }

        // Notify parent
        onSave?.(nodeData.label, {
            label: formData.label,
            state: updatedState as AslState,
        });

        setLoading(false);
        onClose();
    };

    if (!nodeData) return null;

    const Icon = stateTypeIcons[nodeData.stateType] || Play;
    const colors = stateTypeColors[nodeData.stateType] || stateTypeColors.Task;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-10 py-5 border-b border-slate-100 bg-slate-50/50 relative">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colors.bg}`}>
                                        <Icon className={`h-5 w-5 ${colors.text}`} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            {nodeData.stateType} State
                                        </h2>
                                        <p className="text-sm text-slate-500">{nodeData.label}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <p className="text-sm text-slate-500">
                                {readOnly
                                    ? 'View state configuration and properties.'
                                    : 'Configure the state properties and transitions.'}
                            </p>
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 overflow-y-auto px-10 py-6 bg-white">
                            <div className="space-y-5">
                                {/* State Info Card */}
                                <div className={`flex items-center gap-4 p-4 rounded-lg border ${colors.border} ${colors.bg}`}>
                                    <Icon className={`h-8 w-8 ${colors.text}`} />
                                    <div className="flex-1">
                                        <span className="font-semibold text-slate-900">
                                            {nodeData.stateType}
                                        </span>
                                        {nodeData.isStart && (
                                            <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                Start State
                                            </span>
                                        )}
                                        {nodeData.isEnd && (
                                            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                                End State
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Name */}
                                <div className="space-y-2">
                                    <Label>State Name</Label>
                                    <Input
                                        type="text"
                                        value={formData.label}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
                                        disabled={readOnly}
                                        className="w-full"
                                        placeholder="Enter state name"
                                    />
                                </div>

                                {/* Comment */}
                                <div className="space-y-2">
                                    <Label>Comment / Description</Label>
                                    <Textarea
                                        value={formData.comment}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
                                        disabled={readOnly}
                                        placeholder="Optional description of this state"
                                        rows={3}
                                    />
                                </div>

                                {/* Task-specific: Resource */}
                                {nodeData.stateType === 'Task' && (
                                    <div className="space-y-2">
                                        <Label>Resource</Label>
                                        {availableResources ? (
                                            <Select
                                                value={formData.resource || undefined}
                                                onValueChange={(value) =>
                                                    setFormData((prev) => ({ ...prev, resource: value }))
                                                }
                                                disabled={readOnly}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a resource" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {availableResources.agents.length > 0 && (
                                                        <SelectGroup>
                                                            <SelectLabel>Agents</SelectLabel>
                                                            {availableResources.agents.map((a) => (
                                                                <SelectItem key={a.fqcn} value={`agent:${a.fqcn}`}>
                                                                    {a.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectGroup>
                                                    )}
                                                    {availableResources.tools.length > 0 && (
                                                        <SelectGroup>
                                                            <SelectLabel>Tools</SelectLabel>
                                                            {availableResources.tools.map((t) => (
                                                                <SelectItem key={t.fqcn} value={`tool:${t.fqcn}`}>
                                                                    {t.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectGroup>
                                                    )}
                                                    {availableResources.models.length > 0 && (
                                                        <SelectGroup>
                                                            <SelectLabel>Models</SelectLabel>
                                                            {availableResources.models.map((m) => (
                                                                <SelectItem key={m.fqcn} value={`model:${m.fqcn}`}>
                                                                    {m.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectGroup>
                                                    )}
                                                    {availableResources.workflows.length > 0 && (
                                                        <SelectGroup>
                                                            <SelectLabel>Workflows</SelectLabel>
                                                            {availableResources.workflows.map((w) => (
                                                                <SelectItem key={w.fqcn ?? w.name} value={`workflow:${w.fqcn ?? w.name}`}>
                                                                    {w.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectGroup>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Input
                                                type="text"
                                                value={formData.resource}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, resource: e.target.value }))
                                                }
                                                disabled={readOnly}
                                                placeholder="e.g., agent:document-analyzer"
                                            />
                                        )}
                                        <p className="text-xs text-slate-400">
                                            The resource this task invokes (agent, tool, or workflow)
                                        </p>
                                    </div>
                                )}

                                {/* Task-specific: ResultPath */}
                                {nodeData.stateType === 'Task' && (
                                    <div className="space-y-2">
                                        <Label>Result Path</Label>
                                        <Input
                                            type="text"
                                            value={formData.resultPath}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, resultPath: e.target.value }))
                                            }
                                            disabled={readOnly}
                                            placeholder="e.g., $.result"
                                        />
                                        <p className="text-xs text-slate-400">
                                            JSONPath location where results are stored
                                        </p>
                                    </div>
                                )}

                                {/* Wait-specific: Seconds */}
                                {nodeData.stateType === 'Wait' && (
                                    <div className="space-y-2">
                                        <Label>Wait Duration (seconds)</Label>
                                        <Input
                                            type="number"
                                            value={formData.seconds ?? ''}
                                            onChange={(e) =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    seconds: e.target.value ? parseInt(e.target.value) : undefined,
                                                }))
                                            }
                                            disabled={readOnly}
                                            placeholder="Enter seconds"
                                            min={0}
                                        />
                                    </div>
                                )}

                                {/* Fail-specific: Error and Cause */}
                                {nodeData.stateType === 'Fail' && (
                                    <>
                                        <div className="space-y-2">
                                            <Label>Error Code</Label>
                                            <Input
                                                type="text"
                                                value={formData.error}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, error: e.target.value }))
                                                }
                                                disabled={readOnly}
                                                placeholder="e.g., ValidationError"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Error Cause</Label>
                                            <Textarea
                                                value={formData.cause}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({ ...prev, cause: e.target.value }))
                                                }
                                                disabled={readOnly}
                                                placeholder="Human-readable error description"
                                                rows={2}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Raw JSON Preview */}
                                <div className="space-y-2 pt-4 border-t border-slate-200">
                                    <Label>Raw State Definition</Label>
                                    <pre className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs font-mono overflow-auto max-h-80">
                                        {JSON.stringify(nodeData.state, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-10 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                                disabled={loading}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg disabled:opacity-50"
                            >
                                {readOnly ? 'Close' : 'Cancel'}
                            </button>
                            {!readOnly && (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSave();
                                    }}
                                    disabled={loading}
                                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Changes'
                                    )}
                                </button>
                            )}
                        </div>
        </SlideDrawer>
    );
}

